import React, { useState, useEffect, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardBody, Row, Col, Nav, NavItem, NavLink, CardFooter } from "reactstrap";
import { createSelector } from "reselect";
import classnames from "classnames";
import { getNotesPaged, updateNote } from "../../slices/thunks";
import NoResults from "../Parts/NoResults";
import NoteModal from "./NoteModal";

const NotesTable = () => {
    const dispatch = useDispatch();        
    
    const store = createSelector((state) => state.Note, (state) => ({
        pagedNotes: state.pagedNotes,
        paginationNotesInfo: state.paginationNotesInfo,
        pageNoteRequest: state.pageNoteRequest,
        error: state.error,
    }));

    const { pagedNotes, paginationNotesInfo, pageNoteRequest, error } = useSelector(store);

    const user = JSON.parse(sessionStorage.getItem("authUser"));
    const isAdmin = user.role == 'Admin' ? true : false;   

    const [noteList, setNoteList] = useState([]);
    
    const [activeTab, setActiveTab] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    
    const [modal, setModal] = useState(false);
    const [note, setNote] = useState([]);

    const [isEdit, setIsEdit] = useState(false);
    const toggleModal = () => {        
        setModal(!modal);
        setIsEdit(!isEdit);
    };
    
    const editNote = (note) => {
        setNote(note);
        setIsEdit(true);
        toggleModal();
    };

    const archiveNote = (note) => {
        const updatedNote = {...note, isActive: !note.isActive};        
        dispatch(updateNote(updatedNote));
    };
    
    const toggleActiveTab = (tab) => {
        setActiveTab(tab);
        const request = { page: 1, size: 25, statusFilter: tab, isActive: tab != 6, searchInput: pageNoteRequest.searchInput, searchField: pageNoteRequest.searchField };
        sessionStorage.setItem("pageNoteRequest", JSON.stringify(request));
        dispatch(getNotesPaged(request));
    };
    
    const handlePageChange = (page) => {
        setCurrentPage(page);
        const request = { page: page, size: 25, statusFilter: activeTab, isActive: activeTab != 6, searchInput: pageNoteRequest.searchInput, searchField: pageNoteRequest.searchField };
        sessionStorage.setItem("pageNoteRequest", JSON.stringify(request));
        dispatch(getNotesPaged(request));
    };

    useEffect(() => {
        dispatch(getNotesPaged(pageNoteRequest));
        setNoteList(pagedNotes);
        setActiveTab(pageNoteRequest.statusFilter);
    }, []);

    useEffect(() => {
        setNoteList(pagedNotes);
        setCurrentPage(paginationNotesInfo.currentPage);
        setActiveTab(pageNoteRequest.statusFilter);     
    }, [pagedNotes]); 

    const parseDeliveryData = (description) => {
        try {
            return JSON.parse(description);
        } catch (e) {
            return null;
        }
    };

    const renderNoteCard = (item) => {
        // If it's a delivery note (type 1), parse and display delivery info
        if (item.noteType === 1) {
            const deliveryData = parseDeliveryData(item.description);
            if (deliveryData) {
                return (
                    <Col lg={4} md={6} key={item.id}>
                        <Card className="m-2">
                            <CardBody className="p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h5 className="fw-bold mb-0">{item.title}</h5>
                                </div>
                                <div className="text-muted small mb-2">
                                    <div className="mb-1">
                                        <i className="ri-user-line me-1"></i>
                                        <strong>Za:</strong> {deliveryData.nazivKlijenta || 'N/A'}
                                    </div>
                                    <div className="mb-1">
                                        <i className="ri-map-pin-line me-1"></i>
                                        {deliveryData.adresa || ''}{deliveryData.adresa && deliveryData.grad ? ', ' : ''}{deliveryData.grad || ''}
                                    </div>
                                    <div className="mb-1">
                                        <i className="ri-phone-line me-1"></i>
                                        {deliveryData.telefon || 'N/A'}
                                    </div>
                                    {deliveryData.opisNarudzbe && (
                                        <div className="mb-1">
                                            <i className="ri-file-text-line me-1"></i>
                                            <strong>Opis:</strong> {deliveryData.opisNarudzbe}
                                        </div>
                                    )}
                                </div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="text-muted small">
                                        <i className="ri-box-3-line me-1"></i>
                                        {deliveryData.vrstaUsluge || 'N/A'}
                                    </div>
                                    <div className="text-muted small">
                                        <i className="ri-weight-line me-1"></i>
                                        {deliveryData.masa || 0} kg
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <span className="badge bg-info text-uppercase">
                                        KREIRANA
                                    </span>
                                    {deliveryData.vrstaDostave && (
                                        <span className="badge bg-secondary ms-2">
                                            {deliveryData.vrstaDostave}
                                        </span>
                                    )}
                                    {deliveryData.usluguPlaca && (
                                        <span className={`badge ${deliveryData.usluguPlaca === 'posiljalac' ? 'bg-primary' : 'bg-warning'} ms-2`}>
                                            {deliveryData.usluguPlaca === 'posiljalac' ? 'POŠILJALAC' : 'PRIMALAC'}
                                        </span>
                                    )}
                                </div>
                                {deliveryData.iznosNaplate > 0 && (
                                    <div className="mb-2">
                                        <strong className="text-success">Naplata: {deliveryData.iznosNaplate} KM</strong>
                                    </div>
                                )}
                                {(deliveryData.otvaranje || deliveryData.povratZamjenske || deliveryData.povratAmbalaze) && (
                                    <div className="text-muted small">
                                        <strong>Dodatno:</strong> {' '}
                                        {deliveryData.otvaranje && 'Otvaranje '}
                                        {deliveryData.povratZamjenske && 'Povrat zamjenske '}
                                        {deliveryData.povratAmbalaze && 'Povrat ambalaže'}
                                    </div>
                                )}
                                {deliveryData.napomena && (
                                    <div className="text-muted small mt-2">
                                        <i className="ri-chat-3-line me-1"></i>
                                        {deliveryData.napomena}
                                    </div>
                                )}
                            </CardBody>
                            {!item.isActive ? (
                                <CardFooter className="text-center py-2">
                                    <small className="text-muted fw-bold">
                                        Arhivirano: {new Date(item.archivedTimestampUtc).toLocaleString('bs-BA')}
                                    </small>
                                </CardFooter>
                            ) : (
                                <CardFooter className="p-2">
                                    <div className="hstack gap-2">
                                        <button className="btn btn-sm btn-soft-primary w-100" onClick={() => {editNote(item)}}>
                                            <i className="ri-edit-line me-1"></i> Uredi
                                        </button>
                                        <button className="btn btn-sm btn-soft-success w-100" onClick={() => {archiveNote(item)}}>
                                            <i className="ri-archive-line me-1"></i> Arhiviraj
                                        </button>
                                    </div>
                                </CardFooter>
                            )}
                        </Card>
                    </Col>
                );
            }
        }

        // Standard note display for other types
        return (
            <Col lg={3} md={6} key={item.id}>
                <Card className="m-2 text-center">
                    <CardBody className="py-5 px-4" style={{height: "125px"}}>
                        <h5 className="fw-bold">{item.title}</h5>
                        <p className="text-muted pb-1">{item.description}</p>
                    </CardBody>                            
                    {!item.isActive ? (
                        <h5 className="fw-bold">{"Arhivirano: " + item.archivedTimestampUtc.replace('T', ' ').replace('Z', '')}</h5>
                    ) : (
                        <div className="m-3 hstack gap-2">
                            <button className="btn btn-soft-primary w-100" onClick={() => {editNote(item)}}> Uredi </button>
                            <button className="btn btn-soft-success w-100" onClick={() => {archiveNote(item)}}> Arhiviraj </button>
                        </div>
                    )}
                </Card>
            </Col>
        );
    };

    return (
        <Fragment>
            <NoteModal isEdit={isEdit} note={note} show={modal} onCloseClick={toggleModal}/>
            <CardBody className="pt-0 mb-2">
                <Nav className="nav-tabs nav-tabs-custom nav-success">
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab == 1 }, "fw-semibold")} onClick={() => {toggleActiveTab(1)}}> Brza pošta </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab == 2 }, "fw-semibold")} onClick={() => {toggleActiveTab(2)}}> Narudžbe </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab == 3 }, "fw-semibold")} onClick={() => {toggleActiveTab(3)}}> Revers </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab == 4 }, "fw-semibold")} onClick={() => {toggleActiveTab(4)}}> Bilješke dijelovi </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab == 5 }, "fw-semibold")} onClick={() => {toggleActiveTab(5)}}> Ostalo </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames({ active: activeTab == 6 }, "fw-semibold")} onClick={() => {toggleActiveTab(6)}}> Arhiv </NavLink>
                    </NavItem>
                </Nav>
            </CardBody>
            {(!noteList || !noteList.length) ? ( <NoResults /> ) : (<Row className="justify-content-center">
                {(noteList || []).map(item => renderNoteCard(item))}            
                    <div className="hstack gap-3 justify-content-center mt-3">
                        {currentPage <= 1 ? 
                            (<button className="page-item pagination-prev disabled">Prethodna</button>) : 
                            (<button className="page-item pagination-prev" onClick={() => handlePageChange(currentPage - 1)}>Prethodna</button>)
                        }
                        <div className="vr"></div>
                        {currentPage >= paginationNotesInfo.totalPages ? 
                            (<button className="page-item pagination-prev disabled">Sljedeća</button>) : 
                            (<button className="page-item pagination-prev" onClick={() => handlePageChange(currentPage + 1)}>Sljedeća</button>)
                        }
                    </div>
                    <h5 className="mt-4 text-center">{ "Stranica " + currentPage + " od " + paginationNotesInfo.totalPages}</h5>
                </Row>)}
        </Fragment>
    );
};

export default NotesTable;